// Device-local navigation memory: last content location + view per project.
// All localStorage access is guarded (can be absent/throw) like src/lib/google.js.
const KEY = 'caravan.nav'

export function loadNav() {
  try {
    const raw = localStorage.getItem(KEY)
    if (!raw) return { lastRoute: null, viewByProject: {}, openSections: {} }
    const parsed = JSON.parse(raw)
    return {
      lastRoute: parsed.lastRoute ?? null,
      viewByProject: parsed.viewByProject ?? {},
      openSections: parsed.openSections ?? {},
    }
  } catch {
    return { lastRoute: null, viewByProject: {}, openSections: {} }
  }
}

function write(nav) {
  try { localStorage.setItem(KEY, JSON.stringify(nav)) } catch { /* ignore */ }
}

// Record the last *content* location. Settings is deliberately never recorded.
// The `task` overlay key is stripped so resume lands on the board, not a modal.
export function saveLastRoute(route) {
  if (!route || route.type === 'settings') return
  let toSave = route
  if (route.query && 'task' in route.query) {
    const { task, ...rest } = route.query
    toSave = { ...route }
    if (Object.keys(rest).length) toSave.query = rest
    else delete toSave.query
  }
  const nav = loadNav()
  nav.lastRoute = toSave
  write(nav)
}

export function getView(projectId) {
  if (!projectId) return null
  return loadNav().viewByProject[projectId] ?? null
}

// Whether the *derived* project view should be written to the saved preference.
// Only a project route with an explicit non-default view qualifies: a bare
// `#/project/<id>` deep-link carries no ?view= and derives the default 'list',
// and persisting that would clobber a saved board/calendar preference. An
// explicit switch *to* list is recorded at its call site (it likewise leaves
// no ?view= for this effect to observe). Pure — safe to unit-test.
export function shouldPersistProjectView(route, view) {
  return !!route && route.type === 'project' && !!route.projectId && view !== 'list'
}

export function setView(projectId, view) {
  if (!projectId || !view) return
  const nav = loadNav()
  nav.viewByProject = { ...nav.viewByProject, [projectId]: view }
  write(nav)
}

// Accordion open/closed state for the sidebar's collapsible sections
// (projects | speaking | pr). Stored device-local so manual expand/collapse
// survives reloads. Merge-per-key so an explicit `false` is preserved.
export function getOpenSections() {
  return loadNav().openSections
}

export function setOpenSection(key, open) {
  if (!key) return
  const nav = loadNav()
  nav.openSections = { ...nav.openSections, [key]: !!open }
  write(nav)
}

// Startup route for a fresh open (no hash). pref: 'continue' | 'home'.
// projectExists(id) guards a saved project that has since been deleted.
export function resolveStartup(pref, nav, projectExists = () => true) {
  if (pref !== 'continue') return { type: 'home' }
  const last = nav && nav.lastRoute
  if (!last || !last.type) return { type: 'home' }
  if (last.type === 'project' && !projectExists(last.projectId)) return { type: 'home' }
  return last
}
